import React from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import "./Table.scss";

/**
 * The house table. Use the markup you already have and give it the classes:
 *   <TableWrap>                      a white card that scrolls sideways on narrow screens
 *     <table className="ui-table">   label-font heads, 1px row lines, row hover
 *       <th className="ui-table__num"> / <td className="ui-table__num">  right-aligned, tabular numbers
 *       <td className="ui-table__actions">                             the row's buttons, right-aligned
 *       <tr className="is-clickable"> a row that opens something
 *       <tr className="is-selected">  a ticked row
 * and <SortHeader> for sortable heads.
 *
 * Phones: add `ui-table--stack` to the <table> and each row turns into a card (no sideways scrolling):
 *   <td data-label="Captain">          shows its label on the left, the value on the right
 *   <td className="ui-table__stack-title">   the card's first line (name), full width, no label
 *   <td className="ui-table__stack-hide">    left out of the card (e.g. a column already in the title)
 *   <td className="ui-table__actions">       the row's buttons move to the card's top-right corner
 *   <td className="ui-table__check">         a selection checkbox moves to the card's top-left corner
 */
export function TableWrap({ className, children, footer }: { className?: string; children: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <div className={["ui-table-wrap", className].filter(Boolean).join(" ")}>
      <div className="ui-table-wrap__scroll">{children}</div>
      {footer ? <div className="ui-table-wrap__foot">{footer}</div> : null}
    </div>
  );
}

/** A sortable column head: shows the direction when it is the sorted column. */
export function SortHeader({
  label,
  active,
  direction,
  onClick,
  className,
  align = "left",
}: {
  label: React.ReactNode;
  active: boolean;
  direction: "asc" | "desc";
  onClick: () => void;
  className?: string;
  align?: "left" | "center" | "right";
}) {
  const Icon = !active ? ArrowUpDown : direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={className} aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"} style={{ textAlign: align }}>
      <button type="button" className={`ui-table__sort${active ? " is-active" : ""}`} onClick={onClick}>
        {label}
        <Icon size={13} aria-hidden="true" />
      </button>
    </th>
  );
}

export default TableWrap;
